// client/src/config/featureFlags.js
/**
 * Feature Flags Configuration (Runtime)
 *
 * Flags are fetched from the backend API at app startup.
 * Call initFeatureFlags() before using any flags.
 */

import flex from '../api/FlexFetch'

const DEFAULT_FLAGS = {
  SMS_NOTIFICATIONS: false,
  REQUIRE_AVAILABILITY_FOR_PROPOSALS: false,
  DIRECT_MATCH_PROPOSALS: false,
  EMAIL_OUTBOX_ALL_ADMINS: false,
  FEATURE_FLAGS_ALL_ADMINS: false,
}

let _flags = { ...DEFAULT_FLAGS }

/**
 * Initialize feature flags from backend API
 * Must be called in App.js before rendering
 */
export async function initFeatureFlags() {
  try {
    const response = await flex.flexFetch('/api/1.0/config/getFeatureFlags')
    const result = await flex.handleResponse(response)
    if (result.success) {
      _flags = { ...DEFAULT_FLAGS, ...result.data }
      logFeatureFlags()
    }
  } catch (error) {
    console.error('Failed to load feature flags, using defaults:', error)
  }
}

// Export as Proxy to always read current values
export const featureFlags = new Proxy(_flags, {
  get: (target, prop) => _flags[prop] ?? false,
})

/**
 * Check if a feature is enabled
 * @param {string} featureName - Name of the feature flag
 * @returns {boolean}
 */
export function isFeatureEnabled(featureName) {
  return _flags[featureName] === true
}

/**
 * Get all enabled features (useful for debugging)
 * @returns {string[]} Array of enabled feature names
 */
export function getEnabledFeatures() {
  return Object.keys(_flags).filter((key) => _flags[key] === true)
}

/**
 * Log feature flag status to console (development only)
 */
export function logFeatureFlags() {
  if (process.env.NODE_ENV === 'development') {
    console.log('🚩 Feature Flags:', _flags)
    const enabled = getEnabledFeatures()
    if (enabled.length > 0) {
      console.log('✅ Enabled Features:', enabled.join(', '))
    } else {
      console.log('❌ No features enabled (all flags off)')
    }
  }
}

/**
 * Reset flags to defaults (for testing only)
 * @internal
 */
export function resetFeatureFlags() {
  _flags = { ...DEFAULT_FLAGS }
}

/**
 * Update feature flags at runtime (for admin tooling)
 * @param {Object} updates - Partial flag updates
 */
export function setFeatureFlags(updates = {}) {
  _flags = { ..._flags, ...updates }
}

export default featureFlags
